import { useState, useEffect } from 'react'
import { User } from '@/types'
import { loadFromStorage, saveToStorage, removeFromStorage } from '@/utils/helpers'
import { apiClient } from '@/utils/api'
import { endSharedSession, isSharedSessionActive } from '@/utils/sharedDevice'

export interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>(() => {
    const user = loadFromStorage('user', null)
    // Try to load token as plain string first, fallback to JSON parse for old format
    let token = localStorage.getItem('auth_token')
    if (token && token.startsWith('"')) {
      try {
        token = JSON.parse(token)
      } catch {}
    }
    return {
      user,
      token,
      isAuthenticated: !!token
    }
  })

  const login = (user: User, token: string) => {
    const newState = { user, token, isAuthenticated: true }
    setAuthState(newState)
    saveToStorage('user', user)
    // Save token directly as string, not JSON
    localStorage.setItem('auth_token', token)
  }

  const logout = () => {
    // Signing out on a shared family device ends the member session only;
    // SharedDeviceController sends the screen back to the picker.
    if (isSharedSessionActive()) endSharedSession()
    setAuthState({ user: null, token: null, isAuthenticated: false })
    removeFromStorage('user')
    removeFromStorage('auth_token')
    // Clear device authentication data
    removeFromStorage('device_fingerprint')
    removeFromStorage('device_trusted')
    removeFromStorage('last_device_check')
  }

  const updateUser = (user: User) => {
    setAuthState(prev => ({ ...prev, user }))
    saveToStorage('user', user)
  }

  // Refresh user data from API (useful when cached data is stale)
  const refreshUser = async () => {
    if (!authState.token) return null
    try {
      const user = await apiClient.getCurrentUser()
      if (user) {
        updateUser(user)
        return user
      }
    } catch (error) {
      console.error('Failed to refresh user:', error)
    }
    return null
  }

  const loginFromToken = async (token: string) => {
    try {
      // Decode the JWT to check what type it is
      const parts = token.split('.')
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]))

        // Check if this is a platform admin JWT (has is_platform_admin flag)
        if (payload.is_platform_admin === true) {

          // Store the token
          localStorage.setItem('auth_token', token)

          // Create a user object from the platform token claims
          // Note: Platform admins use a simplified User structure
          const user: User = {
            id: payload.user_id || '0', // Use user_id from JWT (platform_users.id)
            username: payload.email,
            display_name: payload.name || payload.email,
            age: 0,
            color_theme: '#3B82F6',
            avatar_url: payload.picture || '',
            total_points: 0,
            weekly_points: 0,
            level: 0,
            xp: 0,
            streak_days: 0,
            last_active: new Date().toISOString(),
            preferences: {},
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            is_parent: false,
            is_platform_admin: true // This is the key flag
          }

          login(user, token)
          return { success: true, user }
        }

        // Check if this is already a family JWT (has user_id and family_id)
        if (payload.user_id && payload.family_id) {
          // Store the token
          localStorage.setItem('auth_token', token)

          // Get user info using this token
          const user = await apiClient.getCurrentUser()

          if (user) {
            login(user, token)
            return { success: true, user }
          } else {
            localStorage.removeItem('auth_token')
            return { success: false, error: 'Failed to retrieve user info' }
          }
        }
      }

      // If we get here, it's a platform JWT that needs to be exchanged
      // DO NOT save the platform token
      localStorage.removeItem('auth_token')

      // Call the oauth-login endpoint to exchange tokens
      const response = await apiClient.oauthLogin(token) as any

      if (response.token) {
        // Now we have the HousePoints JWT
        const housepointsToken = response.token

        // Save the HousePoints token
        localStorage.setItem('auth_token', housepointsToken)

        // Get full user info using the HousePoints token
        const user = await apiClient.getCurrentUser()

        if (user) {
          login(user, housepointsToken)
          return { success: true, user }
        } else {
          localStorage.removeItem('auth_token')
          return { success: false, error: 'Failed to retrieve user info' }
        }
      } else {
        return { success: false, error: 'Invalid OAuth response' }
      }
    } catch (error: any) {
      // Remove any token on error
      localStorage.removeItem('auth_token')
      setAuthState({ user: null, token: null, isAuthenticated: false })
      console.error('loginFromToken exception:', error)

      // Check if it's a 403 (user not found in family)
      if (error.response?.status === 403) {
        const errorMessage = error.response?.data?.error || 'User not found in this family'
        return { success: false, error: errorMessage }
      }

      return { success: false, error: 'Failed to authenticate with OAuth token' }
    }
  }

  return {
    ...authState,
    login,
    logout,
    updateUser,
    refreshUser,
    loginFromToken
  }
}